/**
 * Reward Helper Utilities
 * Utility functions for reward display and formatting
 */

import type { SvgIconComponent } from '@mui/icons-material'
import {
  Image as ImageIcon,
  Link as LinkIcon,
  Article,
  QrCode2,
  LockOpen,
  CardGiftcard,
  TouchApp,
  Schedule,
  CalendarMonth,
  Filter1,
  Percent,
} from '@mui/icons-material'
import {
  RewardInstanceStatus,
  GiftRewardContentType,
  DistributionType,
  ExpirationType,
} from '@api/types/rewards'

/**
 * Get color for reward status chip
 */
export function getStatusColor(
  status: RewardInstanceStatus
): 'success' | 'warning' | 'error' | 'default' | 'info' {
  switch (status) {
    case RewardInstanceStatus.Active:
      return 'success'
    case RewardInstanceStatus.Expired:
      return 'warning'
    case RewardInstanceStatus.FullyRedeemed:
      return 'info'
    case RewardInstanceStatus.Revoked:
      return 'error'
    default:
      return 'default'
  }
}

/**
 * Get icon name for reward status
 */
export function getStatusIconName(status: RewardInstanceStatus): string {
  switch (status) {
    case RewardInstanceStatus.Active:
      return 'CheckCircle'
    case RewardInstanceStatus.Expired:
      return 'Schedule'
    case RewardInstanceStatus.FullyRedeemed:
      return 'Redeem'
    case RewardInstanceStatus.Revoked:
      return 'Cancel'
    default:
      return 'Help'
  }
}

/**
 * Get color for content type chip
 */
export function getContentTypeColor(
  type: GiftRewardContentType
): 'primary' | 'secondary' | 'success' | 'info' | 'warning' {
  switch (type) {
    case GiftRewardContentType.Image:
      return 'primary'
    case GiftRewardContentType.URL:
      return 'info'
    case GiftRewardContentType.Text:
      return 'secondary'
    case GiftRewardContentType.QR_Redeemable:
      return 'success'
    case GiftRewardContentType.Adventure_Unlock:
      return 'warning'
    default:
      return 'primary'
  }
}

/**
 * Get icon name for content type
 */
export function getContentTypeIconName(type: GiftRewardContentType): string {
  switch (type) {
    case GiftRewardContentType.Image:
      return 'Image'
    case GiftRewardContentType.URL:
      return 'Link'
    case GiftRewardContentType.Text:
      return 'Article'
    case GiftRewardContentType.QR_Redeemable:
      return 'QrCode2'
    case GiftRewardContentType.Adventure_Unlock:
      return 'LockOpen'
    default:
      return 'CardGiftcard'
  }
}

/**
 * Get icon component for content type
 */
export function getContentTypeIcon(type: GiftRewardContentType): SvgIconComponent {
  switch (type) {
    case GiftRewardContentType.Image:
      return ImageIcon
    case GiftRewardContentType.URL:
      return LinkIcon
    case GiftRewardContentType.Text:
      return Article
    case GiftRewardContentType.QR_Redeemable:
      return QrCode2
    case GiftRewardContentType.Adventure_Unlock:
      return LockOpen
    default:
      return CardGiftcard
  }
}

/**
 * Get icon component for distribution type
 */
export function getDistributionTypeIcon(type: DistributionType): SvgIconComponent {
  switch (type) {
    case DistributionType.Manual:
      return TouchApp
    case DistributionType.TimeInterval:
      return Schedule
    case DistributionType.SpecificDates:
      return CalendarMonth
    case DistributionType.EveryNth:
      return Filter1
    case DistributionType.Percentage:
      return Percent
    default:
      return CardGiftcard
  }
}

/**
 * Get description for distribution type
 */
export function getDistributionTypeDescription(type: DistributionType): string {
  switch (type) {
    case DistributionType.TimeInterval:
      return 'Release rewards at regular time intervals'
    case DistributionType.SpecificDates:
      return 'Release rewards on specific dates'
    case DistributionType.EveryNth:
      return 'Every Nth completion wins a reward'
    case DistributionType.Percentage:
      return 'A percentage of completers win'
    case DistributionType.Manual:
      return 'Distribute rewards manually'
    default:
      return ''
  }
}

/**
 * Format expiration date for display
 */
export function formatExpirationDate(expiresAt: string | null): string {
  if (!expiresAt) return 'Never expires'

  const date = new Date(expiresAt)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays < 0) {
    return `Expired ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago`
  } else if (diffDays === 0) {
    return 'Expires today'
  } else if (diffDays === 1) {
    return 'Expires tomorrow'
  } else if (diffDays <= 7) {
    return `Expires in ${diffDays} days`
  } else {
    return `Expires ${date.toLocaleDateString()}`
  }
}

/**
 * Format remaining uses for display
 */
export function formatRemainingUses(
  remainingUses: number | null,
  totalUses?: number | null
): string {
  if (remainingUses === null) return 'Unlimited uses'

  if (totalUses !== null && totalUses !== undefined) {
    return `${remainingUses} of ${totalUses} uses remaining`
  }

  return `${remainingUses} use${remainingUses !== 1 ? 's' : ''} remaining`
}

/**
 * Check if reward is claimable (has uses and not expired)
 */
export function isRewardClaimable(
  status: RewardInstanceStatus,
  remainingUses: number | null,
  isExpired: boolean
): boolean {
  if (status !== RewardInstanceStatus.Active) return false
  if (isExpired) return false
  if (remainingUses !== null && remainingUses <= 0) return false
  return true
}

/**
 * Get expiration warning level
 */
export function getExpirationWarningLevel(
  expiresAt: string | null
): 'none' | 'soon' | 'very-soon' | 'expired' {
  if (!expiresAt) return 'none'

  const date = new Date(expiresAt)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays < 0) return 'expired'
  if (diffDays <= 3) return 'very-soon'
  if (diffDays <= 7) return 'soon'
  return 'none'
}
